import { Alert, Badge, List } from '@mantine/core';
import { AlertTriangle } from 'react-feather';

/**
 * MED-975 (E1.3d) — incomplete-state visualization for the structured
 * Session Content Editor.
 *
 * MED-1182 (E1.3f-R1) — Participant Pre-Session Work is now OPTIONAL, so the
 * former "<2 reflection prompts" reason was removed. Session readiness is
 * governed by the AGENDA ALONE (≥1 section, each with name + duration).
 *
 * Two callers:
 *   - SessionIncompleteBanner — page-level Alert near the header / Save area
 *     (AC-MC4). Renders only when the agenda is incomplete.
 *   - SectionIncompleteBadge — inline Badge on a section header (AC-MC5).
 *     Renders only when the section is missing a required field.
 *
 * Per AC-MC3, Save is always allowed for in-progress work; this component
 * is the ONLY enforcement signal — there is no hard block. The coach-prep
 * gating that uses this signal lands in e1.3h (MED-976) and the participant
 * pre-session page gating in E3.1c.
 */

/**
 * The session-completeness state. A session is "ready" only when this flag is
 * false. MED-1182 removed the reflection-prompt reason; the agenda is now the
 * sole completeness input.
 */
export interface SessionIncompleteReasons {
  /** AC-MC1 from e1.3d — any section missing name or duration (or no sections at all). */
  sectionsMissingRequiredFields: boolean;
}

export interface SessionIncompleteBannerProps {
  reasons: SessionIncompleteReasons;
}

export function SessionIncompleteBanner({ reasons }: SessionIncompleteBannerProps) {
  const { sectionsMissingRequiredFields } = reasons;
  if (!sectionsMissingRequiredFields) return null;

  // Lead-in copy summarizes the consequence (downstream surfaces gate on
  // completeness). The List spells out exactly what's missing so the CM
  // knows where to focus.
  return (
    <Alert
      color="warningAmber"
      variant="light"
      icon={<AlertTriangle size={16} />}
      title="Session is incomplete"
      data-testid="agenda-session-incomplete-banner"
    >
      Coaches won&apos;t see session prep and participants won&apos;t see their
      pre-session page until:
      <List size="sm" mt={6}>
        <List.Item data-testid="agenda-incomplete-reason-sections">
          Every agenda section has a name and duration.
        </List.Item>
      </List>
    </Alert>
  );
}

export interface SectionIncompleteBadgeProps {
  isIncomplete: boolean;
}

export function SectionIncompleteBadge({ isIncomplete }: SectionIncompleteBadgeProps) {
  if (!isIncomplete) return null;
  return (
    <Badge
      color="warningAmber"
      variant="light"
      radius="sm"
      tt="uppercase"
      fz={10}
      data-testid="agenda-section-incomplete-badge"
    >
      Incomplete
    </Badge>
  );
}
